import React, { useEffect, useState } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { useProjects } from '../../contexts/ProjectContext';
import { Calendar, Clock, Settings, LinkIcon, Users, ChevronDown, LogOut, CreditCard, Menu, ChevronRight, Link2 } from 'lucide-react';

const Sidebar = () => {
  const { user, signOut } = useAuth();
  const { projects, currentProject, selectProject, isCurrentProjectOnboarded } = useProjects();
  const navigate = useNavigate();
  const location = useLocation();
  const [projectMenuOpen, setProjectMenuOpen] = useState(false);
  const [profileMenuOpen, setProfileMenuOpen] = useState(false);
  const [collapsed, setCollapsed] = useState(false);

  const handleSignOut = async () => {
    await signOut();
    navigate('/login');
  };

  useEffect(() => {
    if (currentProject && !isCurrentProjectOnboarded) {
      navigate('/onboarding/business');
    }
  }, [isCurrentProjectOnboarded]);

  const navItems = [
    { name: 'Content Planner', path: '/dashboard/content-planner', icon: <Calendar size={18} /> },
    { name: 'Content History', path: '/dashboard/content-history', icon: <Clock size={18} /> },
    { name: 'Competitors', path: '/dashboard/competitors', icon: <Users size={18} /> },
    { name: 'Integrations', path: '/dashboard/integrations', icon: <LinkIcon size={18} /> },
    { name: 'Linking Configuration', path: '/dashboard/linking-configuration', icon: <Link2 size={18} /> },
    // { name: 'Image Templates', path: '/dashboard/images', icon: <ImagePlus size={18} /> }, // Will be implemented later
    { name: 'Settings', path: '/dashboard/settings', icon: <Settings size={18} /> },
  ];

  return (
    <div
      className={`bg-white text-gray-800 ${collapsed ? 'w-16' : 'w-64'} flex flex-col shadow-lg border-r border-gray-200 transition-all duration-300 h-screen`}
    >
      {/* Logo and Toggle */}
      <div className={`border-b border-gray-200 ${collapsed ? 'flex flex-col items-center py-3' : 'flex items-center justify-between px-4 py-4'}`}>
        {!collapsed ? (
          <img
            src="/logo.png"
            alt="Hyperrank"
            className="h-8"
            onError={e => {
              e.target.onerror = null;
              e.target.style.display = 'none';
            }}
          />
        ) : (
          <div className="mb-2 flex justify-center">
            <img
              src="/android-chrome-192x192.png"
              alt="Hyperrank"
              className="h-8 w-8"
              onError={e => {
                e.target.onerror = null;
                e.target.style.display = 'none';
              }}
            />
          </div>
        )}

        <button
          onClick={() => setCollapsed(!collapsed)}
          className={`rounded-full hover:bg-gray-100 transition-colors ${collapsed ? 'p-1.5' : 'p-2'}`}
          aria-label={collapsed ? "Expand sidebar" : "Collapse sidebar"}
        >
          <Menu size={collapsed ? 16 : 18} className="text-gray-500" />
        </button>
      </div>

      {/* Project Selector - Only show if not collapsed */}
      {!collapsed && (
        <div className="px-4 py-4 border-b border-gray-200">
          <span className="block text-xs font-medium text-gray-500 uppercase tracking-wider mb-2">Project</span>
          <div className="relative">
            <button
              onClick={() => setProjectMenuOpen(open => !open)}
              className="w-full flex items-center justify-between px-3 py-2 bg-white border border-gray-200 rounded-lg shadow-sm text-sm font-medium text-gray-800 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 transition-all"
            >
              <span className="truncate">
                {currentProject?.business_name || 'Select a project'}
              </span>
              <ChevronDown
                className={`w-4 h-4 text-gray-400 transition-transform duration-200 ${projectMenuOpen ? 'rotate-180' : ''
                  }`}
              />
            </button>

            {projectMenuOpen && (
              <ul className="absolute z-10 mt-1 w-full bg-white border border-gray-200 rounded-lg shadow-lg max-h-60 overflow-auto">
                {projects.map(p => (
                  <li key={p.id}>
                    <button
                      onClick={() => {
                        selectProject(p.id);
                        setProjectMenuOpen(false);
                      }}
                      className={`w-full text-left px-3 py-2 text-sm ${currentProject?.id === p.id
                          ? 'bg-primary-50 text-primary-600 font-medium'
                          : 'text-gray-700 hover:bg-gray-50 hover:text-primary-600'
                        } transition-colors`}
                    >
                      {p.business_name}
                    </button>
                  </li>
                ))}
              </ul>
            )}
          </div>

          {currentProject && (
            <p className="mt-2 text-xs text-gray-500 truncate">
              {currentProject.website}
            </p>
          )}
        </div>
      )}

      {/* Navigation */}
      <nav className="flex-1 px-3 py-4 overflow-y-auto scrollbar-thin scrollbar-thumb-gray-300 scrollbar-track-transparent">
        <ul className="space-y-1">
          {navItems.map(item => (
            <li key={item.path}>
              <Link
                to={item.path}
                className={`group flex items-center ${collapsed ? 'justify-center' : 'justify-start'} px-3 py-2.5 text-sm font-medium rounded-lg transition-colors ${location.pathname === item.path
                    ? 'bg-primary-50 text-primary-600'
                    : 'text-gray-700 hover:bg-gray-100 hover:text-primary-600'
                  }`}
                title={collapsed ? item.name : ""}
              >
                <span className={`${collapsed ? '' : 'mr-3'} text-current`}>{item.icon}</span>
                {!collapsed && <span className="font-medium">{item.name}</span>}
              </Link>
            </li>
          ))}
        </ul>
      </nav>

      {/* User Profile */}
      <div className={`px-3 py-4 border-t border-gray-200 bg-gray-50 ${collapsed ? 'flex justify-center' : ''}`}>
        {collapsed ? (
          <div className="relative">
            <button
              onClick={() => setProfileMenuOpen(open => !open)}
              className="relative rounded-full bg-primary-100 h-9 w-9 flex items-center justify-center text-primary-600 font-medium hover:bg-primary-200 transition-colors"
              aria-label="User menu"
            >
              {user?.email?.charAt(0).toUpperCase() || 'U'}
            </button>

            {profileMenuOpen && (
              <ul className="absolute z-10 bottom-full left-0 mb-2 w-48 bg-white border border-gray-200 rounded-lg shadow-lg py-1">
                <li className="px-3 py-2 text-xs text-gray-500 border-b border-gray-100 truncate">
                  {user?.email}
                </li>
                <li>
                  <button
                    onClick={() => {
                      navigate('/dashboard/subscription');
                      setProfileMenuOpen(false);
                    }}
                    className="w-full flex items-center text-left px-3 py-2 text-sm text-gray-700 hover:bg-gray-50 transition-colors"
                  >
                    <CreditCard size={14} className="mr-2 text-gray-500" />
                    Manage Subscription
                  </button>
                </li>
                <li>
                  <button
                    onClick={() => {
                      handleSignOut();
                      setProfileMenuOpen(false);
                    }}
                    className="w-full flex items-center text-left px-3 py-2 text-sm text-gray-700 hover:bg-gray-50 transition-colors"
                  >
                    <LogOut size={14} className="mr-2 text-gray-500" />
                    Sign Out
                  </button>
                </li>
              </ul>
            )}
          </div>
        ) : (
          <div className="relative">
            <button
              onClick={() => setProfileMenuOpen(open => !open)}
              className="w-full flex items-center px-3 py-2 bg-white border border-gray-200 rounded-lg shadow-sm text-sm hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 transition-all"
            >
              <div className="h-8 w-8 rounded-full bg-primary-100 flex items-center justify-center text-primary-600 font-medium mr-3">
                {user?.email?.charAt(0).toUpperCase() || 'U'}
              </div>
              <div className="flex-1 text-left">
                <p className="font-medium text-gray-800 truncate">{user?.email}</p>
              </div>
              <ChevronDown
                className={`w-4 h-4 text-gray-400 transition-transform duration-200 ${profileMenuOpen ? 'rotate-180' : ''
                  }`}
              />
            </button>

            {profileMenuOpen && (
              <ul className="absolute z-10 bottom-full left-0 mb-2 w-full bg-white border border-gray-200 rounded-lg shadow-lg py-1">
                <li>
                  <button
                    onClick={() => {
                      navigate('/dashboard/subscription');
                      setProfileMenuOpen(false);
                    }}
                    className="w-full flex items-center text-left px-3 py-2 text-sm text-gray-700 hover:bg-gray-50 transition-colors"
                  >
                    <CreditCard size={14} className="mr-2 text-gray-500" />
                    Manage Subscription
                  </button>
                </li>
                <li>
                  <button
                    onClick={() => {
                      handleSignOut();
                      setProfileMenuOpen(false);
                    }}
                    className="w-full flex items-center text-left px-3 py-2 text-sm text-gray-700 hover:bg-gray-50 transition-colors"
                  >
                    <LogOut size={14} className="mr-2 text-gray-500" />
                    Sign Out
                  </button>
                </li>
              </ul>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default Sidebar;
